/**
 * Lets a customer show/hide the vertical filter panel on collection pages
 * (tablet/desktop only — the mobile filter drawer is untouched) to reclaim
 * width for the product grid. Filters are HIDDEN by default on a customer's
 * first visit; once they explicitly show or hide the panel, that choice is
 * remembered per-browser via localStorage and restored before first paint
 * by an inline script in layout/theme.liquid, so there's no flash on repeat
 * visits.
 *
 * Uses a single event-delegated listener on `document` so it keeps working
 * after the collection section is re-rendered/morphed (e.g. when a filter
 * or sort option changes), without needing to re-bind to the button.
 */

const STORAGE_KEY = 'ui:desktopFiltersHidden';
const HIDDEN_ATTR = 'data-hide-desktop-filters';

function isHidden() {
  return document.documentElement.hasAttribute(HIDDEN_ATTR);
}

function setHidden(hidden) {
  document.documentElement.toggleAttribute(HIDDEN_ATTR, hidden);

  try {
    // Store the choice explicitly ('1' = hidden, '0' = shown) so the
    // anti-flash script in layout/theme.liquid can tell "user chose to
    // show them" apart from "no choice made yet" (which defaults to hidden).
    localStorage.setItem(STORAGE_KEY, hidden ? '1' : '0');
  } catch (error) {
    // localStorage unavailable (private browsing, storage disabled, etc).
    // The toggle still works for the current page view.
  }
}

function syncToggleButtons() {
  const hidden = isHidden();
  document.querySelectorAll('[data-filters-visibility-toggle]').forEach((button) => {
    button.setAttribute('aria-expanded', String(!hidden));
  });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-filters-visibility-toggle]');
  if (!button) return;

  const nextHidden = !isHidden();
  setHidden(nextHidden);
  button.setAttribute('aria-expanded', String(!nextHidden));
});

// Sync once on load, then keep in sync across section re-renders/morphs
// (e.g. changing a filter or sort option), since server-rendered markup
// always starts from the button's static default aria-expanded value.
syncToggleButtons();

new MutationObserver(syncToggleButtons).observe(document.body, {
  childList: true,
  subtree: true,
});
